import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { authClient } from "@/lib/auth-client";
import { toast } from "sonner";
import { api } from "@api";
import { useOrgSelection } from "./useOrgSelection";
import { type OnboardingStatusEnum } from "nautika-types";

export function useOnboarding(requiredStep?: OnboardingStatusEnum, emailToCheck?: string) {
  const navigate = useNavigate();
  const { data: session, error, isPending } = authClient.useSession();

  // Utilisation du hook pour la gestion des organisations
  const {
    organizations,
    activeOrg,
    isPending: isLoadingOrgs,
    showOrgSelection,
    selectedOrgId,
    setSelectedOrgId,
    isSettingActive,
    handleSetActiveOrganization,
    onCloseOrgSelection,
  } = useOrgSelection();

  // Hook pour vérifier la disponibilité de l'email
  const emailAvailabilityQuery = api.useQuery(
    "get",
    "/onboarding/check-email/{email}",
    {
      params: {
        path: { email: emailToCheck || "" },
      },
    },
    {
      enabled: !!emailToCheck, // Only run query when emailToCheck is provided
    }
  );

  // Refs pour éviter les actions multiples
  const hasNavigatedRef = useRef(false);
  const hasProcessedOrgsRef = useRef(false);

  // Effet principal pour la navigation et la logique d'onboarding
  useEffect(() => {
    if (isPending || isLoadingOrgs) return;
    if (hasNavigatedRef.current) return;

    // Si pas de session et qu'on n'est pas sur l'étape 1
    if (!session || error) {
      if ((!requiredStep || requiredStep !== "step1") && window.location.pathname.includes("/onboarding")) {
        hasNavigatedRef.current = true;
        navigate("/onboarding", { replace: true });
        return;
      }
    }

    // Si l'onboarding terminé, rediriger vers dashboard si nécessaire
    if (session?.user?.onboarding === "done" && requiredStep !== "done") {
      hasNavigatedRef.current = true;
      navigate("/dash", { replace: true });
      return;
    }

    // Si l'étape actuelle ne correspond pas à l'étape requise
    if (session?.user && requiredStep && session.user.onboarding !== requiredStep) {
      hasNavigatedRef.current = true;
      navigate(`/onboarding/${session.user.onboarding}`, { replace: true });
      return;
    }
  }, [session, error, isPending, requiredStep, isLoadingOrgs, navigate, activeOrg]);

  // Reset des refs quand les données critiques changent
  useEffect(() => {
    if (isPending || isLoadingOrgs) {
      hasNavigatedRef.current = false;
      // Ne reset le processing que si on n'a pas d'organisation active
      if (!activeOrg) {
        hasProcessedOrgsRef.current = false;
      }
    }
  }, [isPending, isLoadingOrgs, activeOrg]);

  const updateOnboardingStep = async (nextStep: OnboardingStatusEnum) => {
    const { error } = await authClient.updateUser({
      onboarding: nextStep,
    });
    hasNavigatedRef.current = true;
    navigate(`/onboarding/${nextStep}`, { replace: true });

    if (error) {
      toast.error("Error updating onboarding step", {
        description: error.message,
      });
    }
  };

  const handleSignOut = async () => {
    if (window.confirm("Are you sure you want to quit? All entered data will be lost.")) {
      await authClient.signOut(
        {},
        {
          onSuccess: () => {
            console.log("Successfully signed out");
            navigate("/login");
          },
          onError: () => {
            toast.error("Failed to sign out");
          },
        }
      );
    }
  };

  return {
    session,
    isPending: isPending || isLoadingOrgs,
    error,
    updateOnboardingStep,
    emailAvailabilityQuery,
    handleSignOut,
    organizations,
    activeOrg,
    // États et fonctions pour la sélection d'organisation
    showOrgSelection,
    selectedOrgId,
    setSelectedOrgId,
    isSettingActive,
    handleSetActiveOrganization,
    onCloseOrgSelection,
  };
}
